import { Capacitor } from '@capacitor/core';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { authEnrollBiometric, authLoginBiometric, authRevokeBiometric } from '@/api/gen-new/auth/auth';
import { BiometricPlatform } from '@/api/gen-new/model';
import { getSession } from '@/auth/AuthSession';
import { isAuthenticated, startSessionFromTokens } from '@/auth/AuthUtils';
import { requestTwoFactorCode } from '@/auth/components/TwoFactorPrompt';

import type { BiometricKind, PromptText } from './biometricDevice';

import { unlockApp } from './appLock';
import { deleteSecret, isBiometricPromptInFlight, readSecret, storeSecret, verifyIdentity } from './biometricDevice';
import { clearEnrollment, dismissBiometricOffer, getEnrollment, saveEnrollment } from './biometricEnrollment';

export type EnableBiometricsResult = 'enabled' | 'cancelled' | 'failed';

export type BiometricSignInResult =
	| 'signed-in'
	| 'cancelled'
	/** The credential is gone or rejected; the enrollment was cleared and only the password is left. */
	| 'revoked'
	| 'failed'
	/** Another prompt is already on screen; the caller should leave its UI as it is. */
	| 'busy';

/** With two-factor authentication on, the API asks for a code before it trusts a device. */
function isTwoFactorCodeRequired(error: unknown): boolean {
	return (
		isAxiosError(error)
		&& (error.response?.data as { message?: string } | undefined)?.message === 'auth.two.factor.code.required'
	);
}

export function biometricMethodName(kind: BiometricKind): string {
	return t(`biometric.method.${kind}`);
}

function promptText(kind: BiometricKind, reasonKey: 'unlockReason' | 'enableReason'): PromptText {
	const method = biometricMethodName(kind);
	return { reason: t(`biometric.${reasonKey}`, { method }), cancel: t('common.cancel') };
}

export async function enableBiometrics(kind: BiometricKind): Promise<EnableBiometricsResult> {
	const session = getSession();
	if (!session?.userId) {
		return 'failed';
	}

	const prompt = promptText(kind, 'enableReason');
	const platform = Capacitor.getPlatform() === 'ios' ? BiometricPlatform.ios : BiometricPlatform.android;
	if (platform === BiometricPlatform.ios && !(await verifyIdentity(prompt))) {
		return 'cancelled';
	}

	const previous = getEnrollment();
	const replaceCredentialId = previous?.userId === session.userId ? previous.credentialId : undefined;
	const enroll = (twoFactorCode?: string) => authEnrollBiometric({ platform, replaceCredentialId, twoFactorCode });
	let credential;
	try {
		credential = await enroll();
	} catch (error) {
		if (!isTwoFactorCodeRequired(error)) {
			return 'failed';
		}
		credential = await requestTwoFactorCode(
			t('twoFactor.prompt.biometric', { method: biometricMethodName(kind) }),
			enroll,
		);
		if (!credential) {
			return 'cancelled';
		}
	}

	const stored = await storeSecret(credential.credentialId, credential.secret, prompt);
	if (stored !== 'ok') {
		void authRevokeBiometric(credential.credentialId).catch(() => undefined);
		return stored === 'cancelled' ? 'cancelled' : 'failed';
	}

	saveEnrollment({
		credentialId: credential.credentialId,
		userId: session.userId,
		kind,
		email: session.email,
		loginMethod: session.loginMethod,
		lockEnabled: true,
	});
	return 'enabled';
}

/**
 * The user turned biometrics off, so the post-sign-in offer stays quiet for that account from now on;
 * Settings is how they turn it back on. The server copy is revoked when a session can authorize it.
 */
export async function disableBiometrics(): Promise<void> {
	const enrollment = getEnrollment();
	if (enrollment) {
		dismissBiometricOffer(enrollment.userId);
	}
	clearEnrollment();
	unlockApp();
	await deleteSecret();

	if (enrollment && isAuthenticated()) {
		await authRevokeBiometric(enrollment.credentialId).catch(() => undefined);
	}
}

async function forgetRevokedEnrollment(): Promise<'revoked'> {
	clearEnrollment();
	await deleteSecret();
	return 'revoked';
}

/** Exchanges the Keychain secret for a new session. */
export async function signInWithBiometrics(): Promise<BiometricSignInResult> {
	const enrollment = getEnrollment();
	if (!enrollment) {
		return 'revoked';
	}
	if (isBiometricPromptInFlight()) {
		return 'busy';
	}

	const read = await readSecret(promptText(enrollment.kind, 'unlockReason'));
	if (read.status === 'missing') {
		return forgetRevokedEnrollment();
	}
	if (read.status !== 'ok') {
		return read.status;
	}

	try {
		const tokens = await authLoginBiometric({
			credentialId: read.credentialId,
			secret: read.secret,
			preferredGroupId: getSession()?.userGroupId,
		});
		await startSessionFromTokens(tokens, { loginMethod: enrollment.loginMethod, email: enrollment.email });
		unlockApp();
		return 'signed-in';
	} catch (error) {
		if (isAxiosError(error) && error.response?.status === 401) {
			return forgetRevokedEnrollment();
		}
		return 'failed';
	}
}

/**
 * Lifts the app lock. A still-valid session unlocks offline after the biometric read; an expired one
 * falls through to a biometric sign-in.
 */
export async function unlockWithBiometrics(): Promise<BiometricSignInResult> {
	if (!isAuthenticated()) {
		return signInWithBiometrics();
	}

	const enrollment = getEnrollment();
	if (!enrollment) {
		return 'revoked';
	}
	if (isBiometricPromptInFlight()) {
		return 'busy';
	}

	const read = await readSecret(promptText(enrollment.kind, 'unlockReason'));
	if (read.status === 'missing' || (read.status === 'ok' && read.credentialId !== enrollment.credentialId)) {
		return forgetRevokedEnrollment();
	}
	if (read.status !== 'ok') {
		return read.status;
	}

	unlockApp();
	return 'signed-in';
}
